import {
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  GitBranch,
  MessageSquare,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';
import {
  type ClientMessage,
  enhancedApi,
  useClientConversationDetailQuery,
} from '@/services/api/notificationsService';

interface ConversationDetailProps {
  matterId: string;
}

/**
 * Format a timestamp for chat display
 */
const formatMessageTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  if (diffDays === 0) {
    return timeStr;
  }
  if (diffDays === 1) {
    return `Yesterday ${timeStr}`;
  }
  if (diffDays < 7) {
    return `${date.toLocaleDateString('en-US', { weekday: 'short' })} ${timeStr}`;
  }
  return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${timeStr}`;
};

/**
 * Expandable badge showing related matters for cross-matter SMS messages.
 * Clicking the badge toggles a list of clickable matter links.
 */
const CrossMatterBadge: React.FC<{ message: ClientMessage }> = ({ message }) => {
  const [expanded, setExpanded] = useState(false);

  if (
    message.related_matter_count == null ||
    message.related_matter_count === 0 ||
    !message.related_matters ||
    message.related_matters.length === 0
  ) {
    return null;
  }

  return (
    <div className="flex flex-col mt-0.5">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className={cn(
          'flex items-center gap-1 px-1.5 py-0.5 rounded-md',
          'text-xs font-medium text-gray-600 bg-gray-100 border border-gray-200',
          'hover:bg-gray-200 transition-colors',
        )}
      >
        <GitBranch className="h-3 w-3 text-gray-600" />
        <span>
          Also on {message.related_matter_count} other{' '}
          {message.related_matter_count === 1 ? 'matter' : 'matters'}
        </span>
        {expanded ? (
          <ChevronUp className="h-3 w-3 text-gray-600" />
        ) : (
          <ChevronDown className="h-3 w-3 text-gray-600" />
        )}
      </button>
      {expanded && (
        <ul className="mt-1 space-y-0.5">
          {message.related_matters.map((rm) => (
            <li key={rm.matter_id}>
              <a
                href={`/matters/${rm.matter_id}/client-comms`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-md transition-colors"
              >
                <ExternalLink className="h-3 w-3" />
                {rm.matter_name || rm.matter_id}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const ConversationDetail: React.FC<ConversationDetailProps> = ({ matterId }) => {
  const dispatch = useDispatch();
  const { data, isLoading, isSuccess } = useClientConversationDetailQuery(
    { matterId },
    { pollingInterval: 60000 },
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasInvalidatedRef = useRef<string | null>(null);

  // Invalidate notifications cache when conversation is first loaded
  // Backend marks SMS notifications as read when fetching conversation detail
  useEffect(() => {
    if (isSuccess && data && hasInvalidatedRef.current !== matterId) {
      hasInvalidatedRef.current = matterId;
      // Small delay to ensure backend has processed the mark-as-read
      const timer = setTimeout(() => {
        dispatch(enhancedApi.util.invalidateTags(['notifications']));
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isSuccess, data, matterId, dispatch]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (data?.messages && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [data?.messages]);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-400">
        <p className="text-sm font-medium">Conversation not found</p>
      </div>
    );
  }

  const displayName = data.client_name || data.matter_name || 'Unknown';

  // Reverse messages for chronological order (API returns newest first)
  const chronologicalMessages = [...(data.messages || [])].reverse();

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-white">
        <div>
          <h2 className="text-sm font-bold text-gray-900">{displayName}</h2>
          {data.matter_name && data.client_name && (
            <p className="text-xs text-gray-500 font-medium">{data.matter_name}</p>
          )}
        </div>
        <Link
          to={`/matters/${matterId}/client-comms`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          View matter
        </Link>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {chronologicalMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <MessageSquare className="w-10 h-10 mb-2" />
            <p className="text-sm font-medium">No messages yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {chronologicalMessages.map((message, index) => {
              const isInbound = message.is_inbound;
              const prevMessage = index > 0 ? chronologicalMessages[index - 1] : null;
              const showTimestamp =
                !prevMessage ||
                new Date(message.timestamp).getTime() - new Date(prevMessage.timestamp).getTime() >
                  5 * 60 * 1000; // 5 minutes

              return (
                <div key={message.id}>
                  {showTimestamp && (
                    <div className="text-center mb-2">
                      <span className="text-[10px] text-gray-400 font-semibold">
                        {formatMessageTime(message.timestamp)}
                      </span>
                    </div>
                  )}
                  <div
                    className={cn(
                      'flex flex-col',
                      isInbound ? 'items-start' : 'items-end',
                    )}
                  >
                    <div
                      className={cn(
                        'max-w-[75%] px-3 py-2 rounded-2xl text-sm font-medium leading-snug',
                        isInbound
                          ? 'bg-gray-100 text-gray-900 rounded-bl-md'
                          : 'bg-indigo-600 text-white rounded-br-md',
                      )}
                    >
                      {message.content}
                    </div>
                    {/* Cross-matter indicator */}
                    <CrossMatterBadge message={message} />
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Footer - Reply CTA */}
      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50">
        <Link
          to={`/matters/${matterId}/client-comms`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-[13px] font-semibold rounded-lg transition-colors"
        >
          Reply to client
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
};
